const { ImapFlow } = require('imapflow');

// Racines de mots-clés (FR/EN) cherchées dans le SUJET.
// IMAP fait une recherche "sous-chaîne" : on garde donc des racines courtes et
// non redondantes ('verif' couvre verify/verified/verification), ce qui divise
// par deux le nombre de requêtes tout en élargissant la couverture.
const SUBJECT_KEYWORDS = [
  'welcome', 'bienvenue',
  'sign up', 'signup', 'signing up', 'signed up',
  'regist', 'inscri',
  'verif', 'vérifi',
  'confirm',
  'activat', 'activez',
  'password', 'mot de passe',
  'nouveau compte', 'compte créé', 'création de compte',
  'account created', 'your new account', 'new account',
  'get started', 'première connexion',
  // Une confirmation de commande ou une facture prouve l'existence d'un compte
  // aussi sûrement qu'un email de bienvenue — souvent mieux, car ces emails-là
  // sont rarement supprimés.
  'commande', 'order', 'reçu', 'receipt', 'facture', 'invoice',
  // Les emails de connexion et les codes à usage unique désignent eux aussi un
  // compte existant. On garde des formes longues pour « code » : seul, le mot
  // ramènerait tous les codes promo de la boîte.
  'connexion', 'sign in', 'log in', 'login',
  'code de vérification', 'code de connexion', 'verification code', 'security code'
];

// Fragments cherchés dans l'expéditeur (From) — beaucoup d'emails d'inscription
// ont un sujet peu explicite mais un expéditeur très caractéristique.
const FROM_KEYWORDS = [
  'no-reply', 'noreply', 'donotreply', 'do-not-reply',
  'welcome', 'account', 'verify', 'confirm', 'registration', 'signup'
];

// Requête Gmail (X-GM-RAW) équivalente, exécutée en UNE seule fois sur "Tous les
// messages" quand le serveur est Gmail : incomparablement plus rapide que de
// boucler sur chaque dossier.
// Elle doit couvrir les mêmes termes que SUBJECT_KEYWORDS et FROM_KEYWORDS :
// une requête plus étroite ferait détecter moins de comptes sur Gmail que sur
// les autres messageries, sans que rien ne le signale.
const GMAIL_RAW_QUERY = [
  'subject:(welcome OR bienvenue OR verify OR verification OR verified OR confirm',
  'OR confirmation OR confirmez OR vérifiez OR activate OR activation OR activez',
  'OR password OR "mot de passe" OR inscription OR inscrit OR register OR registration',
  'OR signup OR "sign up" OR "signing up" OR "signed up"',
  'OR "nouveau compte" OR "compte créé" OR "création de compte"',
  'OR "account created" OR "new account" OR "get started" OR "première connexion"',
  'OR commande OR order OR reçu OR receipt OR facture OR invoice',
  'OR connexion OR "sign in" OR "log in" OR login',
  'OR "code de vérification" OR "code de connexion" OR "verification code" OR "security code")',
  'OR from:(noreply OR no-reply OR donotreply OR do-not-reply OR welcome',
  'OR account OR accounts OR verify OR confirm OR registration OR signup)'
].join(' ');

// Préfixes de sous-domaines "techniques" à retirer pour retrouver le nom du service
// (accounts.google.com -> google.com, mail.notion.so -> notion.so, ...)
const TECH_SUBDOMAIN_PREFIXES = new Set([
  'mail', 'mails', 'email', 'emails', 'no-reply', 'noreply', 'notifications', 'notification',
  'notify', 'accounts', 'account', 'mailer', 'mailing', 'news', 'newsletter', 'info', 'hello',
  'team', 'support', 'alert', 'alerts', 'update', 'updates', 'service', 'services', 'msg',
  'messages', 'message', 'em', 'e', 'members', 'member', 'do-not-reply', 'donotreply', 'auto',
  'automated', 'system', 'send', 'sender', 'sending', 'smtp', 'bounce', 'reply', 'replies',
  'contact', 'client', 'clients', 'espace', 'my', 'app', 'apps', 'web', 'www', 'go', 'link',
  'links', 'track', 'tracking', 'marketing', 'promo', 'promotions', 'campaign', 'campaigns'
]);

function baseDomain(domain) {
  const parts = domain.toLowerCase().split('.').filter(Boolean);
  while (parts.length > 2 && TECH_SUBDOMAIN_PREFIXES.has(parts[0])) {
    parts.shift();
  }
  return parts.join('.');
}

function serviceNameFromDomain(domain) {
  const label = domain.split('.')[0] || domain;
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/**
 * Extrait l'adresse de désinscription de l'en-tête List-Unsubscribe.
 * Sa forme est « <https://…>, <mailto:…> » : on préfère le lien web, plus
 * simple à suivre pour l'utilisateur qu'un email à rédiger.
 */
function parseUnsubscribe(raw) {
  const d = parseDesinscription(raw);
  return d ? (d.web || d.mailto) : null;
}

/**
 * Toutes les façons de se désinscrire qu'annonce un email :
 *  - web    : la page de désinscription (List-Unsubscribe, lien https) ;
 *  - mailto : l'adresse à qui écrire pour se désinscrire ;
 *  - unClic : l'expéditeur accepte la désinscription « en un clic » (RFC 8058,
 *             en-tête List-Unsubscribe-Post) — Scanyt peut alors la faire
 *             lui-même, sans ouvrir de page.
 */
function parseDesinscription(raw) {
  if (!raw) return null;
  const texte = String(raw);
  const ligne = (texte.match(/^list-unsubscribe:([\s\S]*?)(?=^\S|(?![\s\S]))/im) || [])[1] || texte;
  const liens = [...ligne.matchAll(/<([^>]+)>/g)].map(m => m[1].trim());
  const web = liens.find(l => /^https:/i.test(l)) || liens.find(l => /^http:/i.test(l)) || null;
  const mailto = liens.find(l => /^mailto:/i.test(l)) || null;
  if (!web && !mailto) return null;
  const unClic = Boolean(web) && /^list-unsubscribe-post:\s*list-unsubscribe\s*=\s*one-click/im.test(texte);
  return { web, mailto, unClic };
}

// Domaines de messagerie personnelle. Recevoir un email de gmail.com, c'est
// un correspondant qui écrit — pas un compte chez Gmail. On ne les écarte que
// des expéditeurs PRÉSUMÉS : si un email de bienvenue vient vraiment de l'un
// d'eux, il est confirmé et conservé.
const PERSONAL_MAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com',
  'hotmail.com', 'hotmail.fr', 'outlook.com', 'outlook.fr', 'live.com', 'live.fr', 'msn.com',
  'yahoo.com', 'yahoo.fr', 'ymail.com', 'aol.com',
  'icloud.com', 'me.com', 'mac.com',
  'free.fr', 'orange.fr', 'wanadoo.fr', 'sfr.fr', 'neuf.fr', 'laposte.net',
  'bbox.fr', 'numericable.fr', 'aliceadsl.fr',
  'proton.me', 'protonmail.com', 'pm.me'
]);

// Un expéditeur jamais confirmé n'est retenu que s'il se comporte comme un
// service : adresse automatique, newsletter, ou plusieurs envois.
const PRESUMED_MIN_COUNT = 3;

// Adresses d'envoi automatiques : le signe le plus net qu'on a affaire à une
// machine, donc à un service, et non à une personne.
const AUTO_SENDER = /(^|[._-])(no-?reply|donotreply|do-not-reply|ne-?pas-?repondre|nepasrepondre|noreponse|mailer|notification|notifications|newsletter)([._-]|$)/i;

/**
 * Ce message porte-t-il les marques d'une création de compte ?
 *
 * C'est exactement le test que fait la recherche IMAP, mais appliqué ici, sur
 * l'enveloppe déjà récupérée. En mode « tous les expéditeurs » il n'y a aucune
 * recherche par mot-clé : c'est lui qui sépare un compte CONFIRMÉ (le scan
 * normal l'aurait trouvé) d'un expéditeur seulement PRÉSUMÉ.
 */
function envelopeConfirme(envelope) {
  if (!envelope) return false;
  const sujet = String(envelope.subject || '').toLowerCase();
  if (SUBJECT_KEYWORDS.some(k => sujet.includes(k))) return true;
  const from = envelope.from && envelope.from[0];
  // La recherche IMAP porte sur l'en-tête From entier : nom affiché compris.
  const entete = `${(from && from.name) || ''} ${(from && from.address) || ''}`.toLowerCase();
  return FROM_KEYWORDS.some(k => entete.includes(k));
}

function mergeEnvelopeIntoResults(results, envelope, unsubscribe, confirme, lu = true) {
  const from = envelope && envelope.from && envelope.from[0];
  if (!from || !from.address || !from.address.includes('@')) return;
  const domain = from.address.split('@')[1];
  if (!domain) return;
  const bd = baseDomain(domain);
  const date = envelope.date ? new Date(envelope.date) : null;
  const subject = envelope.subject || '';

  if (!results.has(bd)) {
    results.set(bd, {
      domain: bd,
      serviceName: serviceNameFromDomain(bd),
      firstSeen: date,
      lastSeen: date,
      count: 0,
      sampleSubject: subject,
      newsletter: false,
      unsubscribe: null,
      unsubscribeMailto: null,
      unsubscribeOneClick: false,
      newsletterCount: 0,
      newsletterUnread: 0,
      lastNewsletter: null,
      // Confirmé = un email au moins prouve la création d'un compte. Sinon
      // l'expéditeur est seulement présumé (mode « tous les expéditeurs »).
      confirmed: false,
      auto: false,
      messages: []
    });
  }
  const entry = results.get(bd);
  entry.count += 1;
  if (AUTO_SENDER.test(String(from.address).split('@')[0] || '')) entry.auto = true;
  if (confirme) {
    // Le sujet montré en exemple doit être celui qui prouve le compte, pas la
    // dernière promo reçue.
    if (!entry.confirmed) entry.sampleSubject = subject;
    entry.confirmed = true;
  }
  // Un service dont au moins un email porte un lien de désinscription est
  // considéré comme une newsletter. On garde le meilleur moyen de s'en
  // désinscrire : un lien « en un clic » d'abord, puis une page web, puis un
  // email. On compte aussi ses envois, pour dire combien on en reçoit.
  const desabo = typeof unsubscribe === 'string'
    ? { web: /^https?:/i.test(unsubscribe) ? unsubscribe : null, mailto: /^mailto:/i.test(unsubscribe) ? unsubscribe : null, unClic: false }
    : unsubscribe;
  if (desabo && (desabo.web || desabo.mailto)) {
    entry.newsletter = true;
    entry.newsletterCount += 1;
    // Une newsletter jamais ouverte est le meilleur argument pour s'en défaire.
    if (!lu) entry.newsletterUnread += 1;
    if (date && (!entry.lastNewsletter || date > entry.lastNewsletter)) entry.lastNewsletter = date;
    // Meilleur lien web : « en un clic » d'abord, puis le plus récent (les
    // jetons de désinscription expirent parfois).
    if (desabo.web) {
      const meilleur = !entry.unsubscribe || /^mailto:/i.test(entry.unsubscribe)
        || (desabo.unClic && !entry.unsubscribeOneClick)
        || (desabo.unClic === entry.unsubscribeOneClick && date && (!entry.dateLien || date > entry.dateLien));
      if (meilleur) {
        entry.unsubscribe = desabo.web;
        entry.unsubscribeOneClick = desabo.unClic;
        entry.dateLien = date;
      }
    }
    if (desabo.mailto && !entry.unsubscribeMailto) entry.unsubscribeMailto = desabo.mailto;
    if (!entry.unsubscribe && desabo.mailto) entry.unsubscribe = desabo.mailto;
  }
  if (date && (!entry.firstSeen || date < entry.firstSeen)) entry.firstSeen = date;
  if (date && (!entry.lastSeen || date > entry.lastSeen)) entry.lastSeen = date;

  // On garde le détail des emails pour pouvoir les afficher et y renvoyer.
  // messageId permet un lien direct vers l'email dans les webmails qui le
  // supportent (Gmail) ; ailleurs on retombe sur une recherche par sujet.
  entry.messages.push({
    subject,
    date: date ? date.toISOString() : null,
    from: from.address,
    fromName: from.name || '',
    messageId: envelope.messageId ? String(envelope.messageId).replace(/^<|>$/g, '') : null
  });
}

// Plafond par défaut du nombre d'emails analysés. Au-delà, le budget est
// réparti entre les années (voir selectionnerUids) : il vaut mieux un plafond
// large, les enveloppes se récupèrent par paquets et coûtent peu.
const DEFAULT_MAX_MESSAGES = 25000;

/**
 * Répartit un budget entre des groupes de tailles inégales sans qu'un gros
 * groupe affame les petits : chacun reçoit une part égale, et ce qu'un groupe
 * n'utilise pas (parce qu'il compte moins de messages que sa part) est
 * redistribué aux autres. Les petits groupes sont servis en premier, ce sont
 * eux qui libèrent du surplus.
 *
 * tailles : Map clé -> nombre d'éléments disponibles
 * Renvoie une Map clé -> nombre de places accordées.
 */
function repartirBudget(tailles, budget) {
  const parts = new Map();
  for (const cle of tailles.keys()) parts.set(cle, 0);
  let reste = Math.max(0, budget);
  const groupes = [...tailles.entries()].sort((a, b) => a[1] - b[1]);

  // 1) Part égale pour tous, les plus petits groupes d'abord : ce sont eux qui
  //    libèrent du surplus, puisqu'ils demandent moins que leur part.
  //    Ce partage suppose au moins une place par groupe ; sinon il ne servirait
  //    que les derniers de la liste, et le tourniquet ci-dessous s'en charge.
  if (reste >= groupes.length) {
    let nb = groupes.length;
    for (const [cle, taille] of groupes) {
      const part = Math.min(taille, Math.floor(reste / nb));
      parts.set(cle, part);
      reste -= part;
      nb -= 1;
    }
  }

  // 2) Le reliquat — miettes des divisions entières, ou budget trop petit pour
  //    donner une place à chaque groupe — est distribué une place à la fois,
  //    des clés les plus PETITES aux plus grandes. Sur des années, cela veut
  //    dire les plus anciennes d'abord : ce sont les comptes oubliés, la raison
  //    d'être du scan, alors qu'un service récent réécrira de toute façon.
  const parCle = [...tailles.keys()].sort((a, b) => (a > b ? 1 : a < b ? -1 : 0));
  while (reste > 0) {
    let distribue = 0;
    for (const cle of parCle) {
      if (reste <= 0) break;
      if (parts.get(cle) >= tailles.get(cle)) continue; // ce groupe est servi
      parts.set(cle, parts.get(cle) + 1);
      reste -= 1;
      distribue += 1;
    }
    if (!distribue) break; // plus personne ne peut rien prendre
  }
  return parts;
}

/**
 * Choisit quels messages analyser quand la recherche en a trouvé plus que le
 * plafond.
 *
 * Garder simplement la fin de la liste (les plus récents) revenait à jeter les
 * plus vieux emails — c'est-à-dire exactement les comptes oubliés que l'outil
 * cherche. On donne donc sa part à chaque ANNÉE : une passe de récupération
 * très légère (la date interne seule, ni enveloppe ni en-tête) suffit à savoir
 * de quand date chaque message, et le budget est ensuite réparti entre les
 * années. Dans une année donnée, on garde les plus récents.
 */
async function selectionnerUids(client, uids, budget, onProgress, progressBase) {
  if (uids.length <= budget) return uids;

  const parAnnee = new Map();
  let vus = 0;
  const pas = Math.max(1, Math.ceil(uids.length / 100));
  try {
    for await (const msg of client.fetch(uids, { uid: true, internalDate: true }, { uid: true })) {
      const annee = msg.internalDate ? new Date(msg.internalDate).getUTCFullYear() : 0;
      if (!parAnnee.has(annee)) parAnnee.set(annee, []);
      parAnnee.get(annee).push(msg.uid);
      vus += 1;
      if (onProgress && vus % pas === 0) {
        onProgress({ ...progressBase, phase: 'dating', messagesProcessed: vus, messagesTotal: uids.length });
      }
    }
  } catch (err) {
    // Serveur qui refuse cette récupération : on retombe sur l'ancien
    // comportement plutôt que d'abandonner le scan.
    return uids.slice(-budget);
  }
  if (!parAnnee.size) return uids.slice(-budget);

  const tailles = new Map([...parAnnee].map(([annee, liste]) => [annee, liste.length]));
  const parts = repartirBudget(tailles, budget);
  const gardes = [];
  for (const [annee, liste] of parAnnee) {
    const part = parts.get(annee) || 0;
    if (part <= 0) continue;
    liste.sort((a, b) => a - b);
    gardes.push(...liste.slice(-part)); // les plus récents de cette année-là
  }
  return gardes.sort((a, b) => a - b);
}

/**
 * Récupère les enveloppes des UIDs donnés dans le dossier courant et les agrège.
 * Rapporte la progression au fil de l'eau (utile pour la barre de chargement).
 */
async function fetchAndMerge(client, results, uids, onProgress, progressBase, confirmedUids = null) {
  let processed = 0;
  const total = uids.length;
  // Une centaine de points de mesure quel que soit le volume : la barre avance
  // aussi régulièrement sur 200 messages que sur 8000.
  const step = Math.min(25, Math.max(1, Math.ceil(total / 100)));
  // On demande l'en-tête de désinscription en même temps que l'enveloppe :
  // c'est le marqueur fiable d'une newsletter, et il coûte quelques octets.
  const champs = { envelope: true, uid: true, flags: true, headers: ['list-unsubscribe', 'list-unsubscribe-post'] };
  for await (const msg of client.fetch(uids, champs, { uid: true })) {
    const brut = msg.headers ? msg.headers.toString() : '';
    // En mode classique, l'UID vient d'une recherche par mot-clé : c'est déjà
    // une preuve. Le test sur l'enveloppe rattrape le reste (newsletters).
    const confirme = (confirmedUids ? confirmedUids.has(msg.uid) : false)
      || envelopeConfirme(msg.envelope);
    // Sans drapeaux (serveur qui ne les renvoie pas), on suppose l'email lu :
    // mieux vaut ne rien affirmer que d'accuser à tort.
    const lu = !msg.flags || [...msg.flags].some(f => String(f).toLowerCase() === '\\seen');
    mergeEnvelopeIntoResults(results, msg.envelope, parseDesinscription(brut), confirme, lu);
    processed += 1;
    if (onProgress && processed % step === 0) {
      onProgress({ ...progressBase, phase: 'fetching', messagesProcessed: processed, messagesTotal: total });
    }
  }
  if (onProgress) {
    onProgress({ ...progressBase, phase: 'fetching', messagesProcessed: processed, messagesTotal: total });
  }
  return processed;
}

/**
 * Chemin rapide Gmail : une seule requête X-GM-RAW sur "Tous les messages".
 * Retourne null si ce n'est pas un serveur Gmail ou si la requête échoue,
 * pour laisser la main au scan générique.
 */
/**
 * Recherche les messages porteurs d'un en-tête List-Unsubscribe, c'est-à-dire
 * les newsletters. Elles n'ont aucune raison de contenir les mots-clés d'une
 * création de compte : sans cette requête dédiée, elles resteraient invisibles.
 * Renvoie un tableau d'UID, éventuellement vide si le serveur refuse.
 */
async function searchNewsletters(client, searchBase) {
  try {
    const uids = await client.search({ ...searchBase, header: { 'list-unsubscribe': '' } }, { uid: true });
    return uids || [];
  } catch (err) {
    return []; // serveur qui ne sait pas chercher par en-tête : tant pis
  }
}

async function tryGmailFastPath(client, results, searchBase, maxMessages, onProgress, options) {
  const { includeNewsletters, allSenders } = options;
  const hasGmailExt = client.capabilities && client.capabilities.has('X-GM-EXT-1');
  if (!hasGmailExt) return null;

  // Le dossier "Tous les messages" est localisé (All Mail / Tous les messages…),
  // on le retrouve donc par son flag spécial plutôt que par son nom.
  let allMailPath = null;
  try {
    const list = await client.list();
    const allMail = list.find(m => m.specialUse === '\\All');
    allMailPath = allMail ? allMail.path : null;
  } catch (err) {
    return null;
  }
  if (!allMailPath) return null;

  let lock;
  try {
    lock = await client.getMailboxLock(allMailPath);
  } catch (err) {
    return null;
  }

  try {
    if (onProgress) {
      onProgress({ phase: 'searching', folderName: allMailPath, folderIndex: 0, foldersTotal: 1, mode: 'gmail' });
    }
    // Mode exhaustif : aucune recherche par mot-clé, on prend tout et on trie
    // ensuite sur l'enveloppe. C'est même plus rapide côté serveur.
    const motsCles = allSenders
      ? null
      : new Set(await client.search({ ...searchBase, gmraw: GMAIL_RAW_QUERY }, { uid: true }) || []);
    const ensemble = allSenders
      ? new Set(await client.search({ ...searchBase, all: true }, { uid: true }) || [])
      : new Set(motsCles);
    // Le lien de désinscription est récupéré avec chaque enveloppe : en mode
    // exhaustif les newsletters ressortent sans recherche dédiée.
    if (includeNewsletters && !allSenders) {
      for (const uid of await searchNewsletters(client, searchBase)) ensemble.add(uid);
    }
    const uids = Array.from(ensemble).sort((a, b) => a - b);
    if (!uids.length) return 0;
    const base = { folderName: allMailPath, folderIndex: 0, foldersTotal: 1, mode: 'gmail' };
    const uidList = await selectionnerUids(client, uids, maxMessages, onProgress, base);
    return await fetchAndMerge(client, results, uidList, onProgress, base, motsCles);
  } catch (err) {
    // Requête Gmail refusée : on repasse au scan générique.
    return null;
  } finally {
    lock.release();
  }
}

/**
 * Liste les dossiers sélectionnables de la boîte.
 */
async function listSelectableFolders(client) {
  const list = await client.list();
  const paths = list
    .filter(m => !m.flags || (!m.flags.has('\\Noselect') && !m.flags.has('\\NonExistent')))
    .map(m => m.path);
  return Array.from(new Set(paths));
}

/**
 * Scan générique d'un dossier déjà verrouillé : recherche par sujet puis par
 * expéditeur, puis récupération des enveloppes.
 */
async function scanCurrentFolder(client, results, searchBase, remainingBudget, onProgress, progressBase, options) {
  if (remainingBudget <= 0) return 0;
  const { includeNewsletters, allSenders } = options;

  // Mode exhaustif : une seule recherche « tous les messages », le tri se fait
  // ensuite sur les enveloppes.
  if (allSenders) {
    let tousUids = [];
    try {
      tousUids = await client.search({ ...searchBase, all: true }, { uid: true }) || [];
    } catch (err) {
      return 0;
    }
    if (!tousUids.length) return 0;
    if (onProgress) {
      onProgress({ ...progressBase, phase: 'searching', termsDone: 1, termsTotal: 1 });
    }
    const tri = tousUids.slice().sort((a, b) => a - b);
    const choisis = await selectionnerUids(client, tri, remainingBudget, onProgress, progressBase);
    return await fetchAndMerge(client, results, choisis, onProgress, progressBase);
  }

  const matchedUids = new Set();
  if (includeNewsletters) {
    for (const uid of await searchNewsletters(client, searchBase)) matchedUids.add(uid);
  }
  const motsCles = new Set();
  const allTerms = SUBJECT_KEYWORDS.length + FROM_KEYWORDS.length;
  let termsDone = 0;

  for (const kw of SUBJECT_KEYWORDS) {
    try {
      const uids = await client.search({ ...searchBase, subject: kw }, { uid: true });
      if (uids) for (const uid of uids) { matchedUids.add(uid); motsCles.add(uid); }
    } catch (err) {
      // Un mot-clé qui échoue ne doit pas interrompre le scan.
    }
    termsDone += 1;
    if (onProgress) {
      onProgress({ ...progressBase, phase: 'searching', termsDone, termsTotal: allTerms });
    }
  }

  for (const frag of FROM_KEYWORDS) {
    try {
      const uids = await client.search({ ...searchBase, from: frag }, { uid: true });
      if (uids) for (const uid of uids) { matchedUids.add(uid); motsCles.add(uid); }
    } catch (err) {
      // idem
    }
    termsDone += 1;
    if (onProgress) {
      onProgress({ ...progressBase, phase: 'searching', termsDone, termsTotal: allTerms });
    }
  }

  if (matchedUids.size === 0) return 0;

  // Même principe : le budget restant est réparti entre les années du dossier.
  const tous = Array.from(matchedUids).sort((a, b) => a - b);
  const uidList = await selectionnerUids(client, tous, remainingBudget, onProgress, progressBase);
  return await fetchAndMerge(client, results, uidList, onProgress, progressBase, motsCles);
}

/**
 * Se connecte à la boîte mail en IMAP et recense les services ayant écrit à
 * cette adresse lors d'une création de compte / confirmation / reset.
 *
 * mailbox :
 *  - '__ALL__' : toute la boîte (chemin rapide Gmail si disponible, sinon
 *                parcours de tous les dossiers)
 *  - un nom de dossier précis, ex: 'INBOX'
 *
 * onProgress(info) est appelé régulièrement pour alimenter la barre de progression.
 * Le mot de passe n'est jamais écrit sur disque par ce module.
 */
async function scanMailbox({
  host, port, secure, user, pass, accessToken,
  mailbox = '__ALL__',
  yearsBack,
  maxMessages = DEFAULT_MAX_MESSAGES,
  includeNewsletters = false,
  allSenders = false,
  onProgress = null
}) {
  const options = { includeNewsletters, allSenders };
  const client = new ImapFlow({
    host,
    port,
    secure,
    // Un jeton d'accès (Microsoft) remplace le mot de passe : connexion XOAUTH2.
    auth: accessToken ? { user, accessToken } : { user, pass },
    logger: false,
    connectionTimeout: 20000,
    greetingTimeout: 15000,
    socketTimeout: 60000
  });

  const results = new Map();
  let totalProcessed = 0;

  if (onProgress) onProgress({ phase: 'connecting' });
  await client.connect();

  try {
    const searchBase = {};
    if (yearsBack) {
      const since = new Date();
      since.setFullYear(since.getFullYear() - Number(yearsBack));
      searchBase.since = since;
    }

    if (mailbox === '__ALL__') {
      const gmailProcessed = await tryGmailFastPath(
        client, results, searchBase, maxMessages, onProgress, options
      );
      if (gmailProcessed !== null) {
        totalProcessed = gmailProcessed;
        return finalize(results);
      }
    }

    const folders = mailbox === '__ALL__' ? await listSelectableFolders(client) : [mailbox];

    for (let i = 0; i < folders.length; i++) {
      if (totalProcessed >= maxMessages) break;
      const path = folders[i];
      const progressBase = { folderName: path, folderIndex: i, foldersTotal: folders.length, mode: 'generic' };

      let lock;
      try {
        lock = await client.getMailboxLock(path);
      } catch (err) {
        continue; // dossier illisible : on l'ignore
      }
      try {
        totalProcessed += await scanCurrentFolder(
          client, results, searchBase, maxMessages - totalProcessed, onProgress, progressBase,
          options
        );
      } catch (err) {
        // Une erreur sur un dossier isolé ne fait pas échouer tout le scan.
      } finally {
        lock.release();
      }
    }

    return finalize(results);
  } finally {
    await client.logout().catch(() => client.close());
  }
}

// Nombre d'emails détaillés conservés par service : assez pour comprendre d'où
// vient le compte, sans faire gonfler data/accounts.json indéfiniment.
const MAX_MESSAGES_PER_SERVICE = 50;

/**
 * Un expéditeur jamais confirmé mérite-t-il d'apparaître ? On veut les services
 * dont l'email d'inscription a disparu, pas le carnet d'adresses : on écarte
 * les messageries personnelles, et on exige un signe de service (adresse
 * automatique, newsletter, ou plusieurs envois).
 */
function retenirPresume(entry) {
  if (PERSONAL_MAIL_DOMAINS.has(entry.domain)) return false;
  return Boolean(entry.auto) || Boolean(entry.newsletter) || entry.count >= PRESUMED_MIN_COUNT;
}

function finalize(results) {
  return Array.from(results.values())
    .filter(r => r.confirmed || retenirPresume(r))
    .map(({ dateLien, ...r }) => ({
    ...r,
    firstSeen: r.firstSeen ? r.firstSeen.toISOString() : null,
    lastSeen: r.lastSeen ? r.lastSeen.toISOString() : null,
    newsletter: Boolean(r.newsletter),
    confirmed: Boolean(r.confirmed),
    unsubscribe: r.unsubscribe || null,
    unsubscribeMailto: r.unsubscribeMailto || null,
    unsubscribeOneClick: Boolean(r.unsubscribeOneClick),
    newsletterCount: r.newsletterCount || 0,
    newsletterUnread: r.newsletterUnread || 0,
    lastNewsletter: r.lastNewsletter ? r.lastNewsletter.toISOString() : null,
    messages: (r.messages || [])
      .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')))
      .slice(0, MAX_MESSAGES_PER_SERVICE)
  }));
}

module.exports = {
  scanMailbox, baseDomain, serviceNameFromDomain, repartirBudget, DEFAULT_MAX_MESSAGES,
  envelopeConfirme, retenirPresume, parseDesinscription,
  // Exposés pour les tests (test/scan-modes.test.js) : ils permettent de
  // rejouer un scan complet sur un faux serveur IMAP, sans boîte mail.
  scanCurrentFolder, finalize
};
